/**
 * Offer Room View Component
 * Main view for displaying investment opportunities
 * Extracted from original lines 594-868
 */

import { FC } from "react";
import { Text } from "@mantine/core";
import { ToastContainer } from "react-toastify";
import { COLORS } from "../../../constants/colors";
import {
  BUTTON_STYLES,
  UI_TEXT,
  CAROUSEL_CONFIG,
} from "../StructureNote.constants";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";
import { useCarousel } from "../hooks/useCarousel";
import {
  InvestmentOpportunity,
  InvestorAccountData,
  CurrencyType,
} from "../StructureNote.types";
import VirtualAccountDetails from "./VirtualAccountDetails";
import CarouselNavigation from "./CarouselNavigation";
import InvestmentAmountInput from "./InvestmentAmountInput";

interface OfferRoomViewProps {
  offerRoomData: InvestmentOpportunity[];
  accountData: InvestorAccountData;
  accountGAData: InvestorAccountData;
  selectedData: InvestmentOpportunity | null;
  selectedCurrency: CurrencyType;
  investorSubscribeAmount: string;
  hideNoteSubscribe: boolean;
  onBoxSelect: (data: InvestmentOpportunity) => void;
  onAmountChange: (value: string) => void;
  onSubscribe: () => void;
}

/**
 * Offer Room View container
 * Original logic from lines 594-868
 */
const OfferRoomView: FC<OfferRoomViewProps> = ({
  offerRoomData,
  accountData,
  accountGAData,
  selectedData,
  selectedCurrency,
  investorSubscribeAmount,
  hideNoteSubscribe,
  onBoxSelect,
  onAmountChange,
  onSubscribe,
}) => {
  // Carousel logic (extracted lines 345-364)
  const {
    visibleItems,
    canNavigateBackward,
    canNavigateForward,
    handleNext,
    handlePrev,
  } = useCarousel(offerRoomData, CAROUSEL_CONFIG.ITEMS_PER_PAGE);

  const selectedNoteCurrency = selectedData?.currency || "";
  const showNavigation = offerRoomData?.length > 1;

  return (
    <>
      {/* Header (lines 589-592) */}
      <Text
        style={{
          fontSize: "24px",
          fontWeight: 700,
          marginTop: "10px",
        }}
      >
        {UI_TEXT.OFFER_ROOM_TITLE}
      </Text>

      {/* Description (lines 596-600) */}
      <Text style={{ fontWeight: 400 }}>{UI_TEXT.OFFER_ROOM_DESCRIPTION} </Text>

      {/* Virtual Account Details (lines 601-656) */}
      <VirtualAccountDetails accountGAData={accountGAData} />

      {/* Available Balance Section (lines 657-712) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginTop: 20,
          marginBottom: 10,
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: 20,
            marginBottom: 10,
            gap: 10,
            alignItems: "center",
          }}
        >
          <Text style={{ fontWeight: 800, marginTop: 12, marginBottom: 10 }}>
            {UI_TEXT.AVAILABLE_BALANCE}{" "}
          </Text>
          <Text
            style={{
              color: COLORS.agreementFont,
              fontWeight: "bold",
              fontSize: "22px",
            }}
          >
            <div style={{ display: "flex", gap: 10 }}>
              {selectedCurrency === "SGD" && (
                <span>
                  SGD{" "}
                  {formatNumbersWithThousandSeperators(
                    Number(accountData?.accountBalance?.SGD ?? "0"),
                    true,
                    undefined,
                    "SGD"
                  )}
                </span>
              )}
              {selectedCurrency === "USD" && (
                <span>
                  USD: {(accountData?.accountBalance?.USD ?? 0).toFixed(2)}
                </span>
              )}
            </div>
          </Text>
        </div>
      </div>

      {/* Carousel with Investment Cards (lines 714-798) */}
      <CarouselNavigation
        visibleItems={visibleItems}
        canNavigateBackward={canNavigateBackward}
        canNavigateForward={canNavigateForward}
        onNext={handleNext}
        onPrev={handlePrev}
        selectedData={selectedData}
        onBoxSelect={onBoxSelect}
        hideNoteSubscribe={hideNoteSubscribe}
        showNavigation={showNavigation}
      />

      {/* Investment Amount Input (lines 799-833) */}
      {selectedNoteCurrency !== "" && (
        <InvestmentAmountInput
          selectedNoteCurrency={selectedNoteCurrency}
          investorSubscribeAmount={investorSubscribeAmount}
          maxAmount={selectedData?.amountAvailableForInvestment || "0"}
          onChange={onAmountChange}
        />
      )}

      {/* Subscribe Button (lines 835-866) */}
      {!hideNoteSubscribe && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <button
            style={{
              ...BUTTON_STYLES.subscribe,
              cursor: selectedData ? "pointer" : "not-allowed",
              opacity: selectedData ? 1 : 0.5,
            }}
            onClick={onSubscribe}
            disabled={!selectedData}
          >
            {UI_TEXT.SUBSCRIBE_BUTTON}
          </button>

          <ToastContainer />
        </div>
      )}
    </>
  );
};

export default OfferRoomView;
